import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Divider,
  Avatar,
  Stack,
  Button,
  Badge,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
} from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  Close,
  AutoAwesome,
  AccountCircle,
  Person,
  Store,
  ShoppingCart,
  ExitToApp,
  Dashboard,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import ThemeModeToggle from "../../components/ThemeModeToggle";
import BrandLogo from "../../components/BrandLogo";

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  public: boolean;
}

interface DrawerContentProps {
  user: any;
  handleDrawerToggle: () => void;
  handleSignOut: () => void;
  getItemCount: () => number;
  navItems: NavItem[];
}

const DrawerContent: React.FC<DrawerContentProps> = ({
  user,
  handleDrawerToggle,
  handleSignOut,
  getItemCount,
  navItems,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const isRTL = i18n.language === "ar";

  return (
    <Box
      sx={{
        height: "100%",
        background: theme.palette.background.default,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          p: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box
          component={RouterLink}
          to="/"
          onClick={handleDrawerToggle}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            textDecoration: "none",
            width: 170,
            maxWidth: "70%",
            transition: "transform 0.3s ease, filter 0.3s ease",
            "&:hover": {
              transform: "translateY(-1px)",
              filter: "drop-shadow(0 0 16px rgba(0, 71, 255, 0.18))",
            },
          }}
        >
          <BrandLogo compact />
        </Box>
        <IconButton
          onClick={handleDrawerToggle}
          sx={{ color: theme.palette.text.primary }}
        >
          <Close />
        </IconButton>
      </Box>
      <Divider
        sx={{
          borderColor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.1)"
              : "rgba(0,0,0,0.1)",
        }}
      />

      {/* User Section */}
      <Box sx={{ p: 2 }}>
        {user ? (
          <Box
            sx={{
              p: 2,
              bgcolor: "rgba(0,212,255,0.05)",
              borderRadius: 2,
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Avatar
                sx={{
                  bgcolor: "rgba(0,212,255,0.2)",
                  border: "2px solid rgba(0,212,255,0.3)",
                  width: 48,
                  height: 48,
                }}
              >
                <AccountCircle sx={{ color: "#00d4ff", fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  {t("common.welcome", { defaultValue: "Welcome" })}
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: theme.palette.text.primary }}
                >
                  {user.email}
                </Typography>
              </Box>
            </Box>
            <Stack spacing={1}>
              <Button
                fullWidth
                component={RouterLink}
                to="/profile"
                onClick={handleDrawerToggle}
                startIcon={<Person />}
                sx={{
                  justifyContent: "flex-start",
                  color: "#00d4ff",
                  "&:hover": {
                    bgcolor: "rgba(0,212,255,0.1)",
                  },
                }}
              >
                {t("nav.profile")}
              </Button>
              <Button
                fullWidth
                component={RouterLink}
                to="/orders"
                onClick={handleDrawerToggle}
                startIcon={<Store />}
                sx={{
                  justifyContent: "flex-start",
                  color: "#00d4ff",
                  "&:hover": {
                    bgcolor: "rgba(0,212,255,0.1)",
                  },
                }}
              >
                {t("nav.orders")}
              </Button>
              <Button
                fullWidth
                component={RouterLink}
                to="/cart"
                onClick={handleDrawerToggle}
                startIcon={
                  <Badge
                    badgeContent={getItemCount()}
                    sx={{
                      "& .MuiBadge-badge": {
                        bgcolor: "#ff0080",
                        color: "white",
                        fontWeight: 600,
                      },
                    }}
                  >
                    <ShoppingCart />
                  </Badge>
                }
                sx={{
                  justifyContent: "flex-start",
                  color: "#00d4ff",
                  "&:hover": {
                    bgcolor: "rgba(0,212,255,0.1)",
                  },
                }}
              >
                {t("nav.cart")}
              </Button>
              <Divider sx={{ my: 1 }} />
              <Button
                fullWidth
                onClick={handleSignOut}
                startIcon={<ExitToApp />}
                sx={{
                  justifyContent: "flex-start",
                  color: "#ff0080",
                  "&:hover": {
                    bgcolor: "rgba(255,0,128,0.1)",
                  },
                }}
              >
                {t("nav.logout")}
              </Button>
            </Stack>
          </Box>
        ) : (
          <Stack spacing={2} sx={{ mb: 3 }}>
            <Button
              fullWidth
              component={RouterLink}
              to="/login"
              onClick={handleDrawerToggle}
              variant="outlined"
              sx={{
                borderRadius: "8px",
                borderColor: "rgba(0,212,255,0.5)",
                color: "#00d4ff",
                "&:hover": {
                  borderColor: "#00d4ff",
                  bgcolor: "rgba(0,212,255,0.1)",
                },
              }}
            >
              {t("auth.signIn")}
            </Button>
            <Button
              fullWidth
              component={RouterLink}
              to="/register"
              onClick={handleDrawerToggle}
              variant="contained"
              sx={{
                borderRadius: "8px",
                background: "linear-gradient(135deg, #00d4ff 0%, #ff0080 100%)",
                boxShadow: "0 4px 20px rgba(0,212,255,0.4)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #00a1cc 0%, #cc0066 100%)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 30px rgba(0,212,255,0.5)",
                },
              }}
            >
              {t("auth.signUp")}
            </Button>
          </Stack>
        )}
      </Box>

      <Divider
        sx={{
          borderColor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.1)"
              : "rgba(0,0,0,0.1)",
        }}
      />

      {/* Navigation Items */}
      <List sx={{ p: 2, flexGrow: 1 }}>
        {navItems.map((item) => {
          if (!item.public && !user) return null;
          const isActive = location.pathname === item.path;

          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                component={RouterLink}
                to={item.path}
                onClick={handleDrawerToggle}
                sx={{
                  borderRadius: "8px",
                  transition: "all 0.3s",
                  position: "relative",
                  overflow: "hidden",
                  bgcolor: isActive ? "rgba(0,212,255,0.1)" : "transparent",
                  [isRTL ? "borderRight" : "borderLeft"]: isActive
                    ? "4px solid #00d4ff"
                    : "4px solid transparent",
                  [isRTL ? "borderLeft" : "borderRight"]: "none",
                  "&:hover": {
                    bgcolor: "rgba(0,212,255,0.15)",
                    "& .MuiListItemIcon-root": {
                      color: "#00d4ff",
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive ? "#00d4ff" : theme.palette.text.secondary,
                    transition: "color 0.3s",
                    minWidth: 40,
                    [isRTL ? "ml" : "mr"]: 2,
                    [isRTL ? "mr" : "ml"]: 0,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  sx={{
                    "& .MuiListItemText-primary": {
                      fontWeight: isActive ? 600 : 400,
                      color: isActive ? "#00d4ff" : theme.palette.text.primary,
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
        {user?.role === "admin" && (
          <ListItem disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              component={RouterLink}
              to="/admin/dashboard"
              onClick={handleDrawerToggle}
              sx={{
                borderRadius: "8px",
                transition: "all 0.3s",
                bgcolor: "rgba(255,0,128,0.1)",
                [isRTL ? "borderRight" : "borderLeft"]: "4px solid #ff0080",
                [isRTL ? "borderLeft" : "borderRight"]: "none",
                "&:hover": {
                  bgcolor: "rgba(255,0,128,0.2)",
                },
              }}
            >
              <ListItemIcon sx={{ color: "#ff0080", minWidth: 40 }}>
                <Dashboard />
              </ListItemIcon>
              <ListItemText
                primary={t("nav.admin", { defaultValue: "Admin Dashboard" })}
                sx={{
                  "& .MuiListItemText-primary": {
                    fontWeight: 600,
                    color: "#ff0080",
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        )}

        {user?.role === "seller" && (
          <ListItem disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              component={RouterLink}
              to="/seller/products"
              onClick={handleDrawerToggle}
              sx={{
                borderRadius: "8px",
                transition: "all 0.3s",
                bgcolor: "rgba(0,212,255,0.08)",
                [isRTL ? "borderRight" : "borderLeft"]: "4px solid #00d4ff",
                [isRTL ? "borderLeft" : "borderRight"]: "none",
                "&:hover": {
                  bgcolor: "rgba(0,212,255,0.16)",
                },
              }}
            >
              <ListItemIcon sx={{ color: "#00d4ff", minWidth: 40 }}>
                <Dashboard />
              </ListItemIcon>
              <ListItemText
                primary={t("dashboard.sellerDashboard")}
                sx={{
                  "& .MuiListItemText-primary": {
                    fontWeight: 600,
                    color: "#00a1cc",
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        )}
      </List>

      {/* Language Switcher and Theme Toggle at bottom */}
      <Box
        sx={{
          p: 2,
          borderTop: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary }}
          >
            {t("common.language", { defaultValue: "Language" })}
          </Typography>
          <LanguageSwitcher />
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary }}
          >
            {t("common.theme", { defaultValue: "Theme" })}
          </Typography>
          <ThemeModeToggle />
        </Box>
      </Box>
    </Box>
  );
};

export default DrawerContent;